import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Language = 'en' | 'es';

interface TranslationDictionary {
  [key: string]: string;
}

const translations: Record<Language, TranslationDictionary> = {
  en: {
    // Navigation & General
    'nav.apps': 'Apps',
    'nav.trending': 'Trending',
    'nav.charts': 'Charts',
    'nav.developers': 'Developers',
    'nav.categories': 'Categories',
    'nav.markets': 'Markets',
    'nav.keywords': 'Keywords',
    'nav.compare': 'Compare',
    'nav.aiInsights': 'AI Insights',
    'nav.pricing': 'Pricing',
    'nav.search': 'Search apps, devs...',
    'nav.trackApp': 'Track App',
    'nav.inWatchlist': 'In Watchlist',
    'nav.addToCollection': 'Add to Collection',
    'nav.share': 'Share',
    'nav.signIn': 'Sign In',
    'nav.pro': 'Pro',

    // Entity Tabs
    'tab.overview': 'Overview',
    'tab.appStore': 'App Store',
    'tab.googlePlay': 'Google Play',
    'tab.rankings': 'Rankings',
    'tab.reviews': 'Reviews',
    'tab.keywords': 'Keywords',
    'tab.countries': 'Countries',
    'tab.competitors': 'Competitors',
    'tab.versions': 'Versions',
    'tab.builtWith': 'Built With',
    'tab.activity': 'Activity',

    // Profile Headers & Badges
    'profile.appleMetadata': 'Apple Public Metadata',
    'profile.googlePlayCached': 'Cached / Provider Pending',
    'profile.growthScore': 'Growth Score™',
    'profile.appOutlier': 'App Outlier™',
    'profile.estimatedDownloads': 'Estimated Downloads',
    'profile.estimatedRevenue': 'Estimated Revenue',
    'profile.searchPosition': 'Search Position',
    'profile.categoryPosition': 'Category Position',
    'profile.globalRating': 'Global Rating',
    'profile.storeAvailability': 'Store Availability',
    'profile.latestVersion': 'Latest Version',
    'profile.firstRelease': 'First Release',
    'profile.lastUpdated': 'Last Updated',
    'profile.minOs': 'Minimum OS',
    'profile.contentRating': 'Content Rating',
    'profile.fileSize': 'Bundle Size',
    'profile.estimatedLabel': 'Estimated',
    'profile.modelLabel': 'AppStatsView Model',
    'profile.storefrontSelector': 'Storefront',
    'profile.viewOnAppStore': 'View on App Store',
    'profile.viewOnGooglePlay': 'View on Google Play (Pending)',
    'profile.evidenceVerified': 'Developer Verified',
    'profile.evidenceRepo': 'Public Repository Evidence',
    'profile.evidenceDoc': 'Public Documentation',
    'profile.evidenceSignal': 'Detected Signal',
    'profile.evidenceUnknown': 'Unknown',

    // Reviews Tab
    'reviews.title': 'Public Review Intelligence',
    'reviews.sentimentBreakdown': 'Sentiment Breakdown',
    'reviews.positive': 'Positive',
    'reviews.neutral': 'Neutral',
    'reviews.negative': 'Negative',
    'reviews.velocity': 'Review Velocity',
    'reviews.commonTopics': 'Common Topics',
    'reviews.allReviews': 'Public User Reviews',
    'reviews.demoBadge': 'DEMO',
    'reviews.filterCountry': 'Storefront Filter',
    'reviews.filterStore': 'Store Filter',

    // Built With Tab
    'builtWith.title': 'Public Build Intelligence',
    'builtWith.subtitle': 'Analyzed framework, runtime architecture, and AI engineering signals backed by verifiable public evidence.',
    'builtWith.hq': 'Company HQ',
    'builtWith.devCountry': 'Developer Country',
    'builtWith.framework': 'Client Framework',
    'builtWith.language': 'Programming Language',
    'builtWith.backend': 'Backend Architecture',
    'builtWith.infra': 'Infrastructure & CDN',
    'builtWith.aiTools': 'AI Coding Tools',

    // Activity Feed Tab
    'activity.title': 'Live Intelligence Feed',
    'activity.subtitle': 'Automated telemetry of ranking fluctuations, version releases, review spikes, and market movements.',
    
    // Pro Teasers
    'pro.unlockChart': 'Unlock 1-Year Historical Chart with AppStatsView Pro',
    'pro.unlockKeywords': 'Unlock 500+ Tracked Keywords with AppStatsView Pro',
    'pro.unlockCompetitors': 'Unlock Deep Competitor Radar History with Pro',
    'pro.upgrade': 'Upgrade to Pro',

    // Developer Page
    'dev.publishingStudio': 'Publishing Studio & Developer Intelligence',
    'dev.knownApps': 'Discovered Portfolio Apps',
    'dev.portfolioSize': 'Portfolio Size',
    'dev.avgRating': 'Average Rating',
    'dev.reach': 'International Reach',
    'dev.recentActivity': 'Recent Activity'
  },
  es: {
    // Navigation & General
    'nav.apps': 'Aplicaciones',
    'nav.trending': 'Tendencias',
    'nav.charts': 'Listas',
    'nav.developers': 'Desarrolladores',
    'nav.categories': 'Categorías',
    'nav.markets': 'Mercados',
    'nav.keywords': 'Palabras Clave',
    'nav.compare': 'Comparar',
    'nav.aiInsights': 'Análisis IA',
    'nav.pricing': 'Precios',
    'nav.search': 'Buscar apps, devs...',
    'nav.trackApp': 'Seguir App',
    'nav.inWatchlist': 'En Seguimiento',
    'nav.addToCollection': 'Añadir a Colección',
    'nav.share': 'Compartir',
    'nav.signIn': 'Iniciar Sesión',
    'nav.pro': 'Pro',

    // Entity Tabs
    'tab.overview': 'Resumen',
    'tab.appStore': 'App Store',
    'tab.googlePlay': 'Google Play',
    'tab.rankings': 'Posiciones',
    'tab.reviews': 'Reseñas',
    'tab.keywords': 'Palabras Clave',
    'tab.countries': 'Países',
    'tab.competitors': 'Competidores',
    'tab.versions': 'Versiones',
    'tab.builtWith': 'Tecnología',
    'tab.activity': 'Actividad',

    // Profile Headers & Badges
    'profile.appleMetadata': 'Metadatos Públicos de Apple',
    'profile.googlePlayCached': 'Caché / Proveedor Pendiente',
    'profile.growthScore': 'Puntuación de Crecimiento™',
    'profile.appOutlier': 'App Outlier™',
    'profile.estimatedDownloads': 'Descargas Estimadas',
    'profile.estimatedRevenue': 'Ingresos Estimados',
    'profile.searchPosition': 'Posición de Búsqueda',
    'profile.categoryPosition': 'Posición en Categoría',
    'profile.globalRating': 'Calificación Global',
    'profile.storeAvailability': 'Disponibilidad en Tiendas',
    'profile.latestVersion': 'Última Versión',
    'profile.firstRelease': 'Primer Lanzamiento',
    'profile.lastUpdated': 'Última Actualización',
    'profile.minOs': 'SO Mínimo',
    'profile.contentRating': 'Clasificación de Edad',
    'profile.fileSize': 'Tamaño',
    'profile.estimatedLabel': 'Estimado',
    'profile.modelLabel': 'Modelo AppStatsView',
    'profile.storefrontSelector': 'Tienda de País',
    'profile.viewOnAppStore': 'Ver en App Store',
    'profile.viewOnGooglePlay': 'Ver en Google Play (Pendiente)',
    'profile.evidenceVerified': 'Verificado por Desarrollador',
    'profile.evidenceRepo': 'Evidencia de Repositorio Público',
    'profile.evidenceDoc': 'Documentación Pública',
    'profile.evidenceSignal': 'Señal Detectada',
    'profile.evidenceUnknown': 'Desconocido',

    // Reviews Tab
    'reviews.title': 'Inteligencia Pública de Reseñas',
    'reviews.sentimentBreakdown': 'Distribución de Sentimiento',
    'reviews.positive': 'Positivo',
    'reviews.neutral': 'Neutro',
    'reviews.negative': 'Negativo',
    'reviews.velocity': 'Velocidad de Reseñas',
    'reviews.commonTopics': 'Temas Recurrentes',
    'reviews.allReviews': 'Reseñas Públicas de Usuarios',
    'reviews.demoBadge': 'DEMO',
    'reviews.filterCountry': 'Filtro por Tienda',
    'reviews.filterStore': 'Filtro por Tienda',

    // Built With Tab
    'builtWith.title': 'Inteligencia de Construcción Pública',
    'builtWith.subtitle': 'Framework analizado, arquitectura de runtime y señales de ingeniería respaldadas por evidencia pública verificable.',
    'builtWith.hq': 'Sede de la Empresa',
    'builtWith.devCountry': 'País del Desarrollador',
    'builtWith.framework': 'Framework Cliente',
    'builtWith.language': 'Lenguaje de Programación',
    'builtWith.backend': 'Arquitectura de Backend',
    'builtWith.infra': 'Infraestructura y CDN',
    'builtWith.aiTools': 'Herramientas de IA',

    // Activity Feed Tab
    'activity.title': 'Feed de Inteligencia en Vivo',
    'activity.subtitle': 'Telemetría automatizada de fluctuaciones de ranking, lanzamientos de versión y movimientos de mercado.',

    // Pro Teasers
    'pro.unlockChart': 'Desbloquea gráfico histórico de 1 año con AppStatsView Pro',
    'pro.unlockKeywords': 'Desbloquea más de 500 palabras clave seguidas con Pro',
    'pro.unlockCompetitors': 'Desbloquea historial profundo de competidores con Pro',
    'pro.upgrade': 'Mejorar a Pro',

    // Developer Page
    'dev.publishingStudio': 'Estudio de Publicación e Inteligencia',
    'dev.knownApps': 'Aplicaciones del Portafolio Descubiertas',
    'dev.portfolioSize': 'Tamaño del Portafolio',
    'dev.avgRating': 'Calificación Promedio',
    'dev.reach': 'Alcance Internacional',
    'dev.recentActivity': 'Actividad Reciente'
  }
};

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const I18nContext = createContext<I18nContextType>({
  language: 'en',
  setLanguage: () => {},
  t: (key: string) => key
});

export const I18nProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    try {
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        const saved = localStorage.getItem('appstatsview_lang');
        return saved === 'es' ? 'es' : 'en';
      }
    } catch {
      // Ignore
    }
    return 'en';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try {
      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        localStorage.setItem('appstatsview_lang', lang);
      }
    } catch {
      // Ignore
    }
  };

  const t = (key: string): string => {
    const dict = translations[language] || translations.en;
    return dict[key] || translations.en[key] || key;
  };

  return (
    <I18nContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
